/*
 * Phase 85.1 — Athlete Identity Health + Recalculate widget.
 *
 * A single self-contained component that:
 *   1. Fetches GET /api/athletes/:id/identity-health and renders the
 *      traffic-light badge (🟢/🟡/🔴).
 *   2. Shows the list of Critical Identity Field warnings inline.
 *   3. Opens an Impact Preview modal (Will / Won't change) and, on
 *      confirm, calls POST /api/athletes/:id/recalculate. Idempotent —
 *      the backend returns `was_noop:true` if nothing needed rebuilding.
 *
 * Drop it into any athlete-specific page (Athlete Profile, Athlete 360,
 * Admin Athlete edit modal) via:
 *   <AthleteIdentityHealthCard athleteId={id} canRecalculate={isStaff} />
 *
 * Intentionally NOT placed in the global nav — per user brief §5.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, AlertCircle, CheckCircle2, XCircle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const OVERALL_TONE = {
  green: { bg: 'bg-emerald-50', text: 'text-emerald-800', ring: 'ring-emerald-200', dot: 'bg-pfap-green', label: 'Complete' },
  amber: { bg: 'bg-amber-50',   text: 'text-amber-800',   ring: 'ring-amber-200',   dot: 'bg-amber-500',  label: 'Needs Review' },
  red:   { bg: 'bg-rose-50',    text: 'text-rose-800',    ring: 'ring-rose-200',    dot: 'bg-rose-500',   label: 'Missing Critical Information' },
};

const CHECK_ICON = {
  ok:      <CheckCircle2 size={14} className="text-emerald-600" />,
  warn:    <AlertCircle  size={14} className="text-amber-600" />,
  missing: <XCircle      size={14} className="text-rose-600" />,
};

export const AthleteIdentityHealthCard = ({ athleteId, canRecalculate = false }) => {
  const [health, setHealth] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!athleteId) return;
    try {
      const r = await axios.get(`${API}/api/athletes/${athleteId}/identity-health`);
      setHealth(r.data);
    } catch (e) {
      console.warn('identity-health load failed', e);
    }
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async () => {
    try {
      const r = await axios.get(`${API}/api/athletes/${athleteId}/recalculate/preview`);
      setPreview(r.data);
      setShowModal(true);
    } catch (e) {
      toast.error('Could not load recalculation preview');
    }
  };

  const doRecalculate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/athletes/${athleteId}/recalculate`);
      const wasNoop = r.data?.was_noop;
      toast.success(wasNoop
        ? 'Already up to date — nothing to recalculate.'
        : `Recalculated (${r.data.sections.length} sections updated).`);
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error('Recalculation failed');
    } finally {
      setBusy(false);
    }
  };

  if (!health) return null;
  const tone = OVERALL_TONE[health.overall] || OVERALL_TONE.amber;

  return (
    <div
      className={`rounded-lg border ${tone.bg} ${tone.text} ${tone.ring} ring-1 px-4 py-3 space-y-2`}
      data-testid="identity-health-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${tone.dot}`}
            aria-hidden
          />
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">
              Athlete Identity
            </p>
            <p className="text-sm font-semibold" data-testid="identity-health-status">
              {tone.label}
            </p>
          </div>
        </div>
        {canRecalculate && (
          <Button
            size="sm"
            variant="outline"
            className="bg-white/70 hover:bg-white"
            onClick={openPreview}
            data-testid="recalculate-button"
            title="Recalculate derived data (benchmarks, AI summaries, draft reports). Raw measurements will not change."
          >
            <RefreshCw size={13} className="mr-1.5" /> Recalculate
          </Button>
        )}
      </div>
      <ul className="text-xs space-y-0.5" data-testid="identity-health-checks">
        {health.checks.map((c) => (
          <li key={c.key} className="flex items-start gap-1.5" data-testid={`identity-check-${c.key}`}>
            {CHECK_ICON[c.status]}
            <span>
              <span className="font-medium">{c.label}</span>
              {c.detail ? <span className="opacity-70"> — {c.detail}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {health.benchmark_readiness?.message_key && (
        <p className="text-[11px] italic opacity-80 flex items-start gap-1" data-testid="benchmark-provisional-warning">
          <Info size={11} className="mt-0.5" />
          {health.benchmark_readiness.message_key === 'benchmark.age_provisional_unknown'
            ? 'Benchmark provisional — athlete age has not yet been confirmed.'
            : 'Benchmark provisional — athlete date of birth is estimated.'}
        </p>
      )}

      {/* Impact Preview modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="recalculate-preview-modal">
          <DialogHeader>
            <DialogTitle>Recalculate athlete data</DialogTitle>
            <DialogDescription>
              This will rebuild every derived interpretation of this athlete&apos;s data.
              <span className="block mt-1 opacity-80">
                Raw measurements and finalised reports are never touched.
              </span>
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-3">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-800 mb-1">
                  Will recalculate
                </p>
                <ul className="space-y-1" data-testid="preview-will-change">
                  {preview.will_change.map((s) => (
                    <li key={s} className="flex items-center gap-1.5 text-emerald-800">
                      <CheckCircle2 size={12} />{s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-600 mb-1">
                  Will NOT change
                </p>
                <ul className="space-y-1" data-testid="preview-will-not-change">
                  {preview.will_not_change.map((s) => (
                    <li key={s} className="flex items-center gap-1.5 text-gray-600">
                      <XCircle size={12} />{s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              data-testid="recalculate-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={doRecalculate}
              disabled={busy}
              className="bg-pfap-green hover:opacity-90 text-white"
              data-testid="recalculate-confirm"
            >
              <RefreshCw size={13} className={`mr-1.5 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Recalculating…' : 'Recalculate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
